import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/hooks/useLanguage";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePricingData, type DbPricingPlan, type DbPricingAddon } from "@/hooks/usePricingData";
import { translatePricingString } from "@/lib/pricingTranslations";
import { Skeleton } from "@/components/ui/skeleton";

interface ProductPricingProps {
  productKey: string;
}

export function ProductPricing({ productKey }: ProductPricingProps) {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const [addonsOpen, setAddonsOpen] = useState(false);
  const { plans, addons, loading } = usePricingData(productKey);

  const gridCols =
    plans.length >= 4
      ? "md:grid-cols-2 xl:grid-cols-4"
      : "md:grid-cols-3";

  if (loading) {
    return (
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Skeleton className="h-6 w-40 mx-auto mb-3" />
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-5 w-80 mx-auto" />
          </div>
          <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-96 rounded-2xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (plans.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-secondary/30">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-coral uppercase tracking-widest text-xs font-semibold font-subheading">
            {t("productPages.pricing.tagline")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-primary mt-3">
            {t("productPages.pricing.title")}
          </h2>
          <p className="text-muted-foreground mt-4 text-base leading-relaxed">
            {t("productPages.pricing.subtitle")}
          </p>
        </div>

        {/* Pricing cards */}
        <div className={`grid grid-cols-1 ${gridCols} gap-6 items-stretch`}>
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.1 }}
              className="flex"
            >
              <div
                className={`relative flex flex-col rounded-2xl border bg-card overflow-hidden w-full transition-shadow ${
                  plan.highlighted
                    ? "border-coral/40 shadow-lg"
                    : "hover:shadow-md"
                }`}
              >
                {/* Recommended badge */}
                {plan.highlighted && (
                  <div className="absolute top-0 right-0 z-10">
                    <span className="bg-coral text-coral-foreground text-xs px-3 py-1 rounded-bl-lg font-semibold">
                      {t("productPages.pricing.recommended")}
                    </span>
                  </div>
                )}

                {/* Special badge */}
                {plan.badge && (
                  <div className="bg-brand-yellow/15 text-foreground text-xs text-center py-1.5 font-medium">
                    {plan.badge}
                  </div>
                )}

                <div className="p-6 lg:p-8 flex flex-col flex-1">
                  {/* Plan name */}
                  <h3 className="text-lg font-subheading uppercase tracking-wide text-foreground">
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mt-3 mb-6">
                    <span className="text-3xl font-heading font-bold text-primary">
                      {plan.price}
                    </span>
                    <span className="text-sm text-muted-foreground ml-1">
                      {t("productPages.pricing.perMonth")}
                    </span>
                  </div>

                  <Separator />

                  {/* Features */}
                  <ul className="space-y-3 mt-6">
                    {plan.features.map((feature, fi) => (
                      <li key={fi} className="flex gap-2.5">
                        <CheckCircle2 className="h-4 w-4 text-coral flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{translatePricingString(feature, t)}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Details table */}
                  {plan.details.length > 0 && (
                    <div className="mt-6 bg-secondary/50 rounded-lg p-4">
                      {plan.details.map((detail, di) => (
                        <div
                          key={di}
                          className="flex justify-between text-xs py-1.5"
                        >
                          <span className="text-muted-foreground">
                            {translatePricingString(detail.label, t)}
                          </span>
                          <span className="font-medium text-foreground">
                            {translatePricingString(detail.value, t)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}


                  {/* CTA buttons */}
                  <div className="mt-auto pt-6 space-y-2">
                    <Button
                      className="w-full"
                      variant={plan.highlighted ? "default" : "outline"}
                      asChild
                    >
                      <Link to={getLocalizedPath("/bestall")}>
                        {t("productPages.ctaOrder")}
                      </Link>
                    </Button>
                    <Button
                      className="w-full"
                      variant="ghost"
                      size="sm"
                      asChild
                    >
                      <Link to={getLocalizedPath("/kontakt?type=demo")}>
                        {t("productPages.ctaButton")}
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Addons collapsible */}
        {addons.length > 0 && (
          <div className="mt-12 max-w-4xl mx-auto">
            <Collapsible open={addonsOpen} onOpenChange={setAddonsOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 mx-auto text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      addonsOpen ? "rotate-180" : ""
                    }`}
                  />
                  {t(
                    addonsOpen
                      ? "productPages.pricing.hideAddons"
                      : "productPages.pricing.showAddons"
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-6 bg-card rounded-xl border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[180px]">
                          {t("productPages.pricing.addonColumn")}
                        </TableHead>
                        {plans.map((plan) => (
                          <TableHead
                            key={plan.id}
                            className="text-center min-w-[100px]"
                          >
                            {plan.name}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {addons.map((addon) => (
                        <TableRow key={addon.id}>
                          <TableCell className="text-sm font-medium">
                            {translatePricingString(addon.name, t)}
                          </TableCell>
                          {plans.map((plan) => (
                            <TableCell
                              key={plan.id}
                              className="text-center text-sm"
                            >
                              {translatePricingString(addon.prices[plan.name] || "–", t)}
                            </TableCell>
                          ))}

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </div>
    </section>
  );
}
